import React, { useState } from "react";
import { db } from "../../firebase";
import { updateDoc, doc } from "firebase/firestore";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

const ShoppingTab = ({ trip, theme, onUpdateTrip, isDarkMode }) => {
  const [editingId, setEditingId] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [forceRender, setForceRender] = useState(0);

  const initialFormState = {
    name: "",
    costTHB: "",
    link: "",
    notes: "",
    isBought: false,
  };

  const [formData, setFormData] = useState(initialFormState);

  const handleEditClick = (item) => {
    setFormData({ ...initialFormState, ...item });
    setEditingId(item.id);
    setIsAdding(false);
  };

  const handleCancel = () => {
    setEditingId(null);
    setIsAdding(false);
    setFormData(initialFormState);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const itemData = {
        ...formData,
        id: editingId || `shop_${Date.now()}`,
        costTHB: parseFloat(formData.costTHB || 0),
        order: editingId
          ? trip.shoppingList?.find((i) => i.id === editingId)?.order
          : trip.shoppingList?.length || 0,
      };

      let newList;
      if (editingId) {
        newList = (trip.shoppingList || []).map((i) =>
          i.id === editingId ? itemData : i
        );
      } else {
        newList = [...(trip.shoppingList || []), itemData];
      }

      await updateDoc(doc(db, "trips", trip.id), { shoppingList: newList });
      onUpdateTrip(newList, "shoppingList");
      setForceRender((prev) => prev + 1);
      handleCancel();
    } catch (error) {
      alert("เกิดข้อผิดพลาดในการบันทึกข้อมูล");
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("คุณต้องการลบรายการนี้ใช่หรือไม่?")) {
      const newList = (trip.shoppingList || []).filter((i) => i.id !== id);
      await updateDoc(doc(db, "trips", trip.id), { shoppingList: newList });
      onUpdateTrip(newList, "shoppingList");
      setForceRender((prev) => prev + 1);
    }
  };

  const handleToggleBought = async (item) => {
    const newList = (trip.shoppingList || []).map((i) =>
      i.id === item.id ? { ...i, isBought: !i.isBought } : i
    );

    onUpdateTrip(newList, "shoppingList");
    setForceRender((prev) => prev + 1);

    await updateDoc(doc(db, "trips", trip.id), { shoppingList: newList });
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const items = Array.from(sortedItems);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const updatedItems = items.map((item, index) => ({
      ...item,
      order: index,
    }));

    try {
      await updateDoc(doc(db, "trips", trip.id), {
        shoppingList: updatedItems,
      });
      onUpdateTrip(updatedItems, "shoppingList");
      setForceRender((prev) => prev + 1);
    } catch (error) {
      console.error("Error updating order:", error);
    }
  };

  const sortedItems = [...(trip.shoppingList || [])].sort((a, b) => {
    if (typeof a.order === "number" && typeof b.order === "number")
      return a.order - b.order;
    return 0;
  });

  const inputStyle = {
    padding: "10px 12px",
    borderRadius: "8px",
    border: `1px solid ${isDarkMode ? "#444" : "#ddd"}`,
    backgroundColor: isDarkMode ? "#2c2c2c" : "#fff",
    color: isDarkMode ? "#fff" : "#000",
    fontSize: "14px",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  };

  const labelStyle = {
    fontSize: "12px",
    fontWeight: "bold",
    color: isDarkMode ? "#aaa" : "#666",
    marginBottom: "5px",
    display: "block",
  };

  const formBgStyle = {
    backgroundColor: isDarkMode ? "#1e1e1e" : "#f8f9fa",
    borderRadius: "12px",
    padding: "20px",
    border: `1px solid ${theme?.border || (isDarkMode ? "#333" : "#ddd")}`,
    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
    marginBottom: "15px",
    gridColumn: "1 / -1", // ทำให้ฟอร์มแก้ไขขยายเต็มความกว้าง (ครอบคลุมทั้ง 2 คอลัมน์)
  };

  return (
    <div
      style={{ paddingBottom: "30px", maxWidth: "1000px", margin: "0 auto" }}
    >
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="shopping-list">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              style={{
                // 🌟 เปลี่ยนจาก Flex เป็น CSS Grid เพื่อแบ่ง 2 คอลัมน์
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
                gap: "15px",
                alignItems: "start",
              }}
            >
              {sortedItems.map((item, index) => {
                const isEditingThis = editingId === item.id;
                return (
                  <Draggable
                    key={String(item.id)}
                    draggableId={String(item.id)}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={{
                          ...provided.draggableProps.style,
                          // 🌟 หากไอเทมกำลังถูกแก้ไข ให้กินพื้นที่เต็มความกว้าง
                          gridColumn: isEditingThis ? "1 / -1" : "auto",
                        }}
                      >
                        {isEditingThis ? (
                          /* ================= EDIT MODE ================= */
                          <div style={formBgStyle}>
                            <form onSubmit={handleSave}>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  marginBottom: "15px",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "10px",
                                    flex: 1,
                                  }}
                                >
                                  <input
                                    type="text"
                                    placeholder="ชื่อสิ่งของ (เช่น บัตร JR Pass, เสื้อกันหนาว)"
                                    value={formData.name}
                                    onChange={(e) =>
                                      setFormData({
                                        ...formData,
                                        name: e.target.value,
                                      })
                                    }
                                    style={{
                                      ...inputStyle,
                                      fontWeight: "bold",
                                      fontSize: "16px",
                                    }}
                                    required
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleDelete(item.id)}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    fontSize: "18px",
                                    color: "#e74c3c",
                                    padding: "0 10px",
                                  }}
                                >
                                  🗑️
                                </button>
                              </div>

                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: "20px",
                                }}
                              >
                                <div
                                  style={{
                                    flex: "1 1 300px",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "15px",
                                  }}
                                >
                                  <div>
                                    <label style={labelStyle}>
                                      ค่าใช้จ่ายโดยประมาณ (฿)
                                    </label>
                                    <input
                                      type="number"
                                      step="any"
                                      value={formData.costTHB}
                                      onChange={(e) =>
                                        setFormData({
                                          ...formData,
                                          costTHB: e.target.value,
                                        })
                                      }
                                      style={inputStyle}
                                    />
                                  </div>
                                  <div>
                                    <label style={labelStyle}>
                                      บันทึกเพิ่มเติม
                                    </label>
                                    <textarea
                                      rows="3"
                                      placeholder="ไซส์เสื้อ, จำนวน, หรือรายละเอียดอื่นๆ"
                                      value={formData.notes}
                                      onChange={(e) =>
                                        setFormData({
                                          ...formData,
                                          notes: e.target.value,
                                        })
                                      }
                                      style={{ ...inputStyle, resize: "none" }}
                                    />
                                  </div>
                                </div>

                                <div
                                  style={{
                                    flex: "1 1 300px",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "15px",
                                    borderLeft: `1px solid ${
                                      isDarkMode ? "#444" : "#ddd"
                                    }`,
                                    paddingLeft: "20px",
                                  }}
                                >
                                  <div>
                                    <label style={labelStyle}>
                                      🔗 ลิงก์ร้านค้า / ข้อมูลอ้างอิง
                                    </label>
                                    <input
                                      type="text"
                                      placeholder="วางลิงก์ Shopee/Lazada หรือเว็บอื่นๆ..."
                                      value={formData.link}
                                      onChange={(e) =>
                                        setFormData({
                                          ...formData,
                                          link: e.target.value,
                                        })
                                      }
                                      style={inputStyle}
                                    />
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "10px",
                                      marginTop: "10px",
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      id="boughtEdit"
                                      checked={formData.isBought}
                                      onChange={(e) =>
                                        setFormData({
                                          ...formData,
                                          isBought: e.target.checked,
                                        })
                                      }
                                      style={{
                                        transform: "scale(1.5)",
                                        accentColor: "#2ed573",
                                        cursor: "pointer",
                                      }}
                                    />
                                    <label
                                      htmlFor="boughtEdit"
                                      style={{
                                        fontWeight: "bold",
                                        color: formData.isBought
                                          ? "#2ed573"
                                          : isDarkMode
                                          ? "#aaa"
                                          : "#666",
                                        cursor: "pointer",
                                      }}
                                    >
                                      ซื้อเตรียมไว้เรียบร้อยแล้ว
                                    </label>
                                  </div>
                                </div>
                              </div>

                              <div
                                style={{
                                  display: "flex",
                                  gap: "10px",
                                  marginTop: "20px",
                                  justifyContent: "flex-end",
                                }}
                              >
                                <button
                                  type="button"
                                  onClick={handleCancel}
                                  style={{
                                    padding: "8px 16px",
                                    borderRadius: "8px",
                                    border: `1px solid ${
                                      isDarkMode ? "#555" : "#ccc"
                                    }`,
                                    background: isDarkMode ? "#333" : "white",
                                    color: isDarkMode ? "#fff" : "#000",
                                    cursor: "pointer",
                                  }}
                                >
                                  ยกเลิก
                                </button>
                                <button
                                  type="submit"
                                  style={{
                                    padding: "8px 24px",
                                    borderRadius: "8px",
                                    border: "none",
                                    background: "#3b82f6",
                                    color: "white",
                                    fontWeight: "bold",
                                    cursor: "pointer",
                                  }}
                                >
                                  บันทึก
                                </button>
                              </div>
                            </form>
                          </div>
                        ) : (
                          /* ================= VIEW MODE ================= */
                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "15px",
                              height: "100%", // ให้ความสูงเต็มบล็อกเวลาอยู่คู่กัน
                              opacity: snapshot.isDragging
                                ? 0.8
                                : item.isBought
                                ? 0.6
                                : 1,
                            }}
                          >
                            <div
                              {...provided.dragHandleProps}
                              style={{
                                color: "#bbb",
                                marginTop: "15px",
                                cursor: "grab",
                              }}
                            >
                              ⋮⋮
                            </div>

                            <div
                              style={{
                                flex: 1,
                                height: "100%", // ดันพื้นหลังให้สูงเต็มบล็อก
                                backgroundColor:
                                  theme?.cardBg ||
                                  (isDarkMode ? "#1e1e1e" : "#fff"),
                                borderRadius: "12px",
                                padding: "18px 20px",
                                border: `1px solid ${
                                  snapshot.isDragging
                                    ? "#3b82f6"
                                    : theme?.border || "#eee"
                                }`,
                                borderLeft: item.isBought
                                  ? "4px solid #2ed573"
                                  : `4px solid #ff7979`,
                                position: "relative",
                                transition: "all 0.2s",
                                boxShadow: snapshot.isDragging
                                  ? "0 10px 20px rgba(0,0,0,0.15)"
                                  : "none",
                              }}
                            >
                              <button
                                onClick={() => handleEditClick(item)}
                                style={{
                                  position: "absolute",
                                  top: "15px",
                                  right: "15px",
                                  background: "none",
                                  border: "none",
                                  color: "#888",
                                  fontSize: "18px",
                                  cursor: "pointer",
                                }}
                              >
                                •••
                              </button>

                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "12px",
                                  marginBottom: "8px",
                                  paddingRight: "30px",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={item.isBought || false}
                                  onChange={() => handleToggleBought(item)}
                                  id={`shop_${item.id}`}
                                  style={{
                                    transform: "scale(1.4)",
                                    accentColor: "#2ed573",
                                    cursor: "pointer",
                                  }}
                                />
                                <label
                                  htmlFor={`shop_${item.id}`}
                                  style={{
                                    fontSize: "16px",
                                    fontWeight: "900",
                                    color: theme?.text || "#333",
                                    textDecoration: item.isBought
                                      ? "line-through"
                                      : "none",
                                    opacity: item.isBought ? 0.5 : 1,
                                    cursor: "pointer",
                                    userSelect: "none",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {item.name}
                                </label>
                              </div>

                              {(item.notes || item.link) && (
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "10px",
                                    flexWrap: "wrap",
                                    marginLeft: "35px",
                                    marginBottom: "10px",
                                    opacity: item.isBought ? 0.5 : 1,
                                  }}
                                >
                                  {item.notes && (
                                    <div
                                      style={{
                                        fontSize: "13px",
                                        color: "#888",
                                        whiteSpace: "pre-wrap",
                                      }}
                                    >
                                      {item.notes}
                                    </div>
                                  )}
                                </div>
                              )}

                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  marginLeft: "35px",
                                  borderTop: `1px dashed ${
                                    isDarkMode ? "#444" : "#eee"
                                  }`,
                                  paddingTop: "10px",
                                  opacity: item.isBought ? 0.5 : 1,
                                }}
                              >
                                <div>
                                  {item.link && (
                                    <a
                                      href={item.link}
                                      target="_blank"
                                      rel="noreferrer"
                                      style={{
                                        color: "#3b82f6",
                                        textDecoration: "none",
                                        backgroundColor:
                                          "rgba(59, 130, 246, 0.1)",
                                        padding: "4px 8px",
                                        borderRadius: "6px",
                                        fontSize: "12px",
                                        fontWeight: "bold",
                                      }}
                                    >
                                      🔗 ดูข้อมูล / สั่งซื้อ
                                    </a>
                                  )}
                                </div>
                                <div
                                  style={{
                                    fontSize: "15px",
                                    fontWeight: "bold",
                                    color: "#ff4757",
                                  }}
                                >
                                  {item.costTHB > 0
                                    ? `฿${Number(
                                        item.costTHB
                                      ).toLocaleString()}`
                                    : "-"}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* ================= ADD NEW SHOPPING FORM ================= */}
      {isAdding && (
        <div
          style={{
            ...formBgStyle,
            border: `2px dashed #ff7979`,
            marginTop: "10px",
            gridColumn: "1 / -1", // ขยายกว้างเต็ม
          }}
        >
          <h4 style={{ margin: "0 0 15px 0", color: "#ff7979" }}>
            🛍️ เพิ่มสิ่งที่ต้องเตรียม
          </h4>
          <form onSubmit={handleSave}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "15px",
              }}
            >
              <div style={{ display: "flex", gap: "10px", flex: 1 }}>
                <input
                  type="text"
                  placeholder="ชื่อสิ่งของ (เช่น บัตร JR Pass, เสื้อกันหนาว)"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  style={{
                    ...inputStyle,
                    fontWeight: "bold",
                    fontSize: "16px",
                  }}
                  required
                />
              </div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "20px" }}>
              <div
                style={{
                  flex: "1 1 300px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "15px",
                }}
              >
                <div>
                  <label style={labelStyle}>ค่าใช้จ่ายโดยประมาณ (฿)</label>
                  <input
                    type="number"
                    step="any"
                    value={formData.costTHB}
                    onChange={(e) =>
                      setFormData({ ...formData, costTHB: e.target.value })
                    }
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>บันทึกเพิ่มเติม</label>
                  <textarea
                    rows="3"
                    placeholder="ไซส์เสื้อ, จำนวน, หรือรายละเอียดอื่นๆ"
                    value={formData.notes}
                    onChange={(e) =>
                      setFormData({ ...formData, notes: e.target.value })
                    }
                    style={{ ...inputStyle, resize: "none" }}
                  />
                </div>
              </div>
              <div
                style={{
                  flex: "1 1 300px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "15px",
                  borderLeft: `1px solid ${isDarkMode ? "#444" : "#ddd"}`,
                  paddingLeft: "20px",
                }}
              >
                <div>
                  <label style={labelStyle}>
                    🔗 ลิงก์ร้านค้า / ข้อมูลอ้างอิง
                  </label>
                  <input
                    type="text"
                    placeholder="วางลิงก์ Shopee/Lazada หรือเว็บอื่นๆ..."
                    value={formData.link}
                    onChange={(e) =>
                      setFormData({ ...formData, link: e.target.value })
                    }
                    style={inputStyle}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    marginTop: "10px",
                  }}
                >
                  <input
                    type="checkbox"
                    id="boughtAdd"
                    checked={formData.isBought}
                    onChange={(e) =>
                      setFormData({ ...formData, isBought: e.target.checked })
                    }
                    style={{
                      transform: "scale(1.5)",
                      accentColor: "#2ed573",
                      cursor: "pointer",
                    }}
                  />
                  <label
                    htmlFor="boughtAdd"
                    style={{
                      fontWeight: "bold",
                      color: formData.isBought
                        ? "#2ed573"
                        : isDarkMode
                        ? "#aaa"
                        : "#666",
                      cursor: "pointer",
                    }}
                  >
                    ซื้อเตรียมไว้เรียบร้อยแล้ว
                  </label>
                </div>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                gap: "10px",
                marginTop: "20px",
                justifyContent: "flex-end",
              }}
            >
              <button
                type="button"
                onClick={handleCancel}
                style={{
                  padding: "8px 16px",
                  borderRadius: "8px",
                  border: `1px solid ${isDarkMode ? "#555" : "#ccc"}`,
                  background: isDarkMode ? "#333" : "white",
                  color: isDarkMode ? "#fff" : "#000",
                  cursor: "pointer",
                }}
              >
                ยกเลิก
              </button>
              <button
                type="submit"
                style={{
                  padding: "8px 24px",
                  borderRadius: "8px",
                  border: "none",
                  background: "#ff7979",
                  color: "white",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                บันทึก
              </button>
            </div>
          </form>
        </div>
      )}

      {!isAdding && !editingId && (
        <button
          onClick={() => {
            setFormData(initialFormState);
            setIsAdding(true);
          }}
          style={{
            padding: "15px",
            borderRadius: "12px",
            border: `1px dashed ${isDarkMode ? "#555" : "#bbb"}`,
            background: "transparent",
            color: isDarkMode ? "#aaa" : "#666",
            fontSize: "15px",
            fontWeight: "bold",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginTop: "10px",
            transition: "0.2s",
            width: "100%",
            justifyContent: "center", // เพิ่มจัดกลางให้ปุ่มดูสมดุล
          }}
        >
          ➕ เพิ่มสิ่งที่ต้องเตรียม
        </button>
      )}
    </div>
  );
};

export default ShoppingTab;
